import { createContext, useReducer, PropsWithChildren, Dispatch } from "react";

export enum ActionType {
    SET_INTEGRATIONS = "set_integrations",
    SET_GOOGLE_SPREADSHEET_AND_SHEET = "set_google_spreadsheet_and_sheet",
    CLEAR_GOOGLE_INTEGRATION = "clear_google_integration",
}

interface GoogleSpreadsheetAndSheetPayload {
    spreadsheetID: string;
    sheetID: string;
}

export interface IntegrationsPayload {
    googleDriveIntegration?: boolean;
    googleSpreadsheetAndSheet?: GoogleSpreadsheetAndSheetPayload | null;
    hasDownloadedShortcut?: boolean;
    hasSentTestTransaction?: boolean;
}

interface SetIntegrationsAction {
    type: ActionType.SET_INTEGRATIONS;
    payload: IntegrationsPayload;
}

interface SetGoogleSpreadsheeAndSheetAction {
    type: ActionType.SET_GOOGLE_SPREADSHEET_AND_SHEET;
    payload: GoogleSpreadsheetAndSheetPayload | null;
}

interface ClearGoogleIntegrationAction {
    type: ActionType.CLEAR_GOOGLE_INTEGRATION;
}

type Action =
    | SetIntegrationsAction
    | SetGoogleSpreadsheeAndSheetAction
    | ClearGoogleIntegrationAction;

export interface State {
    googleDriveIntegration?: boolean;
    googleSpreadsheetAndSheet?: GoogleSpreadsheetAndSheetPayload | null;
    hasDownloadedShortcut?: boolean;
    hasSentTestTransaction?: boolean;
}

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case ActionType.SET_INTEGRATIONS:
            localStorage.setItem(
                "googleDriveIntegration",
                JSON.stringify(action.payload.googleDriveIntegration),
            );
            if (action.payload.hasDownloadedShortcut !== undefined) {
                localStorage.setItem(
                    "snapsheet_shortcut_downloaded",
                    JSON.stringify(action.payload.hasDownloadedShortcut),
                );
            }
            if (action.payload.hasSentTestTransaction !== undefined) {
                localStorage.setItem(
                    "snapsheet_test_transaction_sent",
                    JSON.stringify(action.payload.hasSentTestTransaction),
                );
            }

            return {
                ...state,
                ...action.payload,
            };
        case ActionType.SET_GOOGLE_SPREADSHEET_AND_SHEET:
            localStorage.setItem(
                "googleSpreadsheetAndSheet",
                JSON.stringify(action.payload),
            );

            return {
                ...state,
                googleSpreadsheetAndSheet: action.payload,
            };

        case ActionType.CLEAR_GOOGLE_INTEGRATION:
            // Clear only the integration flag, keep spreadsheet/sheet IDs
            // since those are still valid - user just needs to reconnect
            localStorage.removeItem("googleDriveIntegration");

            return {
                ...state,
                googleDriveIntegration: false,
                // Keep googleSpreadsheetAndSheet - don't clear it
            };

        default:
            return state;
    }
};

export const Context = createContext<{
    state: State;
    dispatch: Dispatch<Action>;
}>({
    state: {},
    dispatch: () => {},
});

export const Store = ({ children }: PropsWithChildren<{}>) => {
    const existingGoogleDriveIntegration = localStorage.getItem(
        "googleDriveIntegration",
    );

    const googleSpreadsheetAndSheet = JSON.parse(
        localStorage.getItem("googleSpreadsheetAndSheet") || "null",
    );

    const existingHasDownloadedShortcut = localStorage.getItem(
        "snapsheet_shortcut_downloaded",
    );
    let hasDownloadedShortcut = false;
    if (existingHasDownloadedShortcut) {
        try {
            hasDownloadedShortcut =
                existingHasDownloadedShortcut === "true" ||
                JSON.parse(existingHasDownloadedShortcut) === true;
        } catch {
            hasDownloadedShortcut = existingHasDownloadedShortcut === "true";
        }
    }

    const existingHasSentTestTransaction = localStorage.getItem(
        "snapsheet_test_transaction_sent",
    );
    let hasSentTestTransaction = false;
    if (existingHasSentTestTransaction) {
        try {
            hasSentTestTransaction =
                existingHasSentTestTransaction === "true" ||
                JSON.parse(existingHasSentTestTransaction) === true;
        } catch {
            hasSentTestTransaction =
                existingHasSentTestTransaction === "true";
        }
    }

    const [state, dispatch] = useReducer(reducer, {
        googleDriveIntegration: existingGoogleDriveIntegration === "true",
        googleSpreadsheetAndSheet: googleSpreadsheetAndSheet ?? undefined,
        hasDownloadedShortcut,
        hasSentTestTransaction,
    });

    return (
        <Context.Provider value={{ state, dispatch }}>
            {children}
        </Context.Provider>
    );
};
